import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import InstitutionNavbar from '../components/instituteNavbar';
import VerifiedBadge from '../components/Verified';
import FilterSidebar from '../components/FilterSidebar';
import {
  Lock, Unlock, Mail, Bookmark, MapPin, User, ExternalLink, SearchX
} from 'lucide-react';

const CandidateResults = () => {
  const location = useLocation();
  const searchedCandidates = location.state?.candidates || null;

  const [apiCandidates, setApiCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State to manage local UI updates for actions
  const [candidatesState, setCandidatesState] = useState({});

  const [showResumePopup, setShowResumePopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState('');

  // Fetch candidates from API (supports filters)
  const fetchCandidates = async (filtersObj = {}) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Convert array filters (skills, roles, boards) to multiple query params
      const params = new URLSearchParams();
      Object.entries(filtersObj).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(val => params.append(key, val));
        } else {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/filterCandidates?${params.toString()}`, {
        headers
      });
      setApiCandidates(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch candidates');
      setApiCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Check for attribute-based search (from "Yes" option)
    if (location.state?.jobAttributes) {
      const { skills, roles, boards } = location.state.jobAttributes;
      fetchCandidates({
        skill: skills,
        role: roles,
        board: boards
      });
    }
    // 2. Check for manual candidates pass (from "No" option or legacy)
    else if (Array.isArray(searchedCandidates) && searchedCandidates.length > 0) {
      setApiCandidates(searchedCandidates);
    }
    // 3. Default: Load all
    else {
      fetchCandidates();
    }
  }, [searchedCandidates, location.state]);

  const handleFilterResults = useCallback((filteredCandidates) => {
    setApiCandidates(filteredCandidates);
  }, []);

  // --- Actions ---

  const handleUnlock = async (candidate) => {
    if (candidate.is_unlocked || candidatesState[candidate._id || candidate.id]?.is_unlocked) return;

    const institutionID = localStorage.getItem("institutionID");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/credits/debit-profile`,
        { userId: institutionID, profileId: candidate._id || candidate.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCandidatesState(prev => ({
          ...prev,
          [candidate._id || candidate.id]: { ...prev[candidate._id || candidate.id], is_unlocked: true }
        }));
        toast.success("Candidate unlocked successfully!");
      } else {
        setPopupTitle('Purchase Credits to Unlock');
        setShowResumePopup(true);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        setPopupTitle('Insufficient Balance');
        setShowResumePopup(true);
      } else {
        toast.error("Error unlocking candidate. Please try again.");
      }
    }
  };

  const handleSave = async (candidate) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/institutionfilter/save-candidate`,
        { candidateId: candidate._id || candidate.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const isSaved = response.data.isSaved;
      setCandidatesState(prev => ({
        ...prev,
        [candidate._id || candidate.id]: { ...prev[candidate._id || candidate.id], is_saved: isSaved }
      }));
      toast.success(isSaved ? "Candidate saved!" : "Candidate unsaved.");

    } catch (err) {
      console.error(err);
      toast.error("Error saving candidate. Please try again.");
    }
  };

  const handleInvite = async (candidate) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/institutionfilter/invite-candidate`,
        { candidateId: candidate._id || candidate.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCandidatesState(prev => ({
        ...prev,
        [candidate._id || candidate.id]: { ...prev[candidate._id || candidate.id], is_invited: true }
      }));
      toast.success("Invitation sent!");

    } catch (err) {
      console.error(err);
      toast.error("Error inviting candidate. Please try again.");
    }
  };

  const hasDownloadedResume = (candidate) => {
    return candidate.is_unlocked || candidatesState[candidate._id || candidate.id]?.is_unlocked;
  };

  const isSaved = (candidate) => {
    return candidatesState[candidate._id || candidate.id]?.is_saved ?? candidate.is_saved;
  };

  const isInvited = (candidate) => {
    return candidatesState[candidate._id || candidate.id]?.is_invited ?? candidate.is_invited;
  };

  const handleClosePopup = () => setShowResumePopup(false);
  const handlePurchaseCredits = () => {
    toast.info('Redirecting to purchase credits...');
    handleClosePopup();
  };


  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset page when candidates change (e.g. filter applied)
  useEffect(() => {
    setCurrentPage(1);
  }, [apiCandidates]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = apiCandidates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(apiCandidates.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <InstitutionNavbar />

      <div className="flex flex-1 pt-24">
        {/* Sidebar */}
        <div className="hidden md:block">
          <FilterSidebar
            onResults={handleFilterResults}
            initialFilters={location.state?.initialFilters || {}}
          />
        </div>

        {/* content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="global-font text-xl font-semibold text-gray-800">
              Search Results: {apiCandidates.length} Candidates Found
            </h2>
          </div>

          {loading && <div className="text-center py-8">Loading...</div>}
          {error && <div className="text-center py-8 text-red-500">{error}</div>}

          {/* Empty state */}
          {!loading && !error && apiCandidates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <SearchX size={64} strokeWidth={1.2} />
              <p className="mt-4 text-lg font-medium text-gray-500">No candidates found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search criteria</p>
            </div>
          )}

          <div className="space-y-6">
            {currentItems.map((candidate, idx) => {
              const cId = candidate._id || candidate.id;
              const unlocked = hasDownloadedResume(candidate);
              const saved = isSaved(candidate);
              const invited = isInvited(candidate);

              // Display logic
              const qualification = candidate.qualification?.qualification || "Not Specified";

              const experienceVal = candidate.experience || candidate.work_preference?.experience || 0;
              const noticeVal = candidate.notice_period || candidate.work_preference?.notice_period || "Immediate";
              const salaryVal = candidate.salary ? `${(candidate.salary / 100000).toFixed(1).replace(/\.0$/, '')} LPA` : "N/A";


              return (
                <div key={cId || idx} className="profile-card p-6 mb-5 max-w-full">
                  <div className="flex flex-col md:flex-row gap-6 items-start">

                    {/* Profile Picture */}
                    <div className="shrink-0">
                      {candidate.profilePic ? (
                        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-100">
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${candidate.profilePic.replace(/\\/g, "/")}`}
                            className="w-full h-full object-cover transition-all"
                            style={!unlocked ? { filter: 'blur(6px)' } : {}}
                            alt="Profile"
                            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${candidate.first_name}&background=random` }}
                          />

                        </div>
                      ) : (
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                          <User size={48} />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 space-y-3">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="section-header m-0 text-xl text-[#2563EB]">
                            {candidate.first_name} {candidate.last_name}
                          </h3>
                          {candidate.admin_verified && <VerifiedBadge />}
                          {!unlocked && <Lock size={16} className="text-amber-500" />}
                          {unlocked && <Unlock size={16} className="text-green-500" />}
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin size={12} />
                          {candidate.work_preference?.preferred_location ? `${candidate.work_preference.preferred_location}, ` : ""}
                          {candidate.work_preference?.city || "State N/A"}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 text-sm text-gray-800">
                        <p><strong>Qualification:</strong> {qualification}</p>
                        <p><strong>Experience:</strong> {experienceVal} Years</p>
                        <p><strong>Notice Period:</strong> {noticeVal}</p>
                        <p><strong>Salary:</strong> {salaryVal}</p>

                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 shrink-0 min-w-[140px]">
                      {!unlocked ? (
                        <button
                          onClick={() => handleUnlock(candidate)}
                          className="save-button mt-0 px-4 py-2 text-[0.9rem] flex items-center justify-center gap-2"
                        >
                          <Lock size={14} /> Unlock
                        </button>
                      ) : (
                        <div className="flex items-center justify-center gap-2 px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded">
                          <Unlock size={14} /> Unlocked
                        </div>
                      )}

                      {unlocked && (
                        <>
                          <button
                            onClick={() => handleInvite(candidate)}
                            disabled={invited}
                            className={`flex items-center justify-center gap-2 px-4 py-2 border text-sm font-medium rounded transition-colors ${invited ? 'bg-gray-100 text-gray-400' : 'border-blue-900 text-blue-900 hover:bg-blue-50'}`}
                          >
                            <Mail size={14} /> {invited ? "Invited" : "Invite"}
                          </button>

                          <button
                            onClick={() => handleSave(candidate)}
                            className={`flex items-center justify-center gap-2 px-4 py-2 border text-sm font-medium rounded transition-colors ${saved ? 'bg-blue-50 text-blue-700 border-blue-200' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                          >
                            <Bookmark size={14} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}
                          </button>
                        </>
                      )}

                      {unlocked && (
                        <Link to={`/candidate-details/${candidate._id || candidate.id}`} className="text-center text-xs text-blue-500 mt-1 hover:underline flex items-center justify-center gap-1">
                          View Full Profile <ExternalLink size={10} />
                        </Link>
                      )}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {apiCandidates.length > itemsPerPage && (
            <div className="flex justify-center mt-8 gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                  <button
                    key={number}
                    onClick={() => handlePageChange(number)}
                    className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md ${currentPage === number
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                      }`}
                  >
                    {number}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Credit Popup */}
      {
        showResumePopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-96 transform transition-all scale-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{popupTitle}</h3>
              <p className="text-gray-600 mb-6">You need more credits to perform this action. Would you like to purchase credits now?</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleClosePopup}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchaseCredits}
                  className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors shadow-sm"
                >
                  Buy Credits
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

export default CandidateResults;